import { mapErrorToUserMessage } from '@repo/core';
/**
 * ─── Jewelry Inventory + POS Pages ──────────────────────────
 */

import { useEffect, useState, useCallback } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { jewelryItemRepo, rateCardRepo, salesOrderRepo, jewelryCustomerRepo } from '../../lib/db'
import {
  PriceCalculator,
  ITEM_CATEGORY_LABELS, METAL_TYPE_LABELS, ITEM_STATUS_LABELS,
  MAKING_CHARGE_LABELS, PAYMENT_METHOD_LABELS,
} from '@repo/entity-jewelry'
import { Gem, Plus, ShoppingBag, Calculator, DollarSign, TrendingUp } from 'lucide-react'

// ═══════════════════════════════════════════════════════════════
// Inventory
// ═══════════════════════════════════════════════════════════════

export function JewelryInventoryPage() {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [form, setForm] = useState({
    tagNumber: '', description: '', category: 'ring', metalType: 'gold',
    purityKarat: 18, weightGrams: 0, makingChargeType: 'per_gram' as string,
    makingChargeValue: 0, wastagePercent: 0, costBasis: 0,
    showcaseLocation: '', notes: '',
  })

  const load = useCallback(async () => {
    setLoading(true)
    try { const r = await jewelryItemRepo.findMany({ page: 1, pageSize: 500, sort: [{ field: 'createdAt', direction: 'desc' }] }); setItems('items' in r ? r.items : []) }
    catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  async function save() {
    setSaving(true)
    try {
      await jewelryItemRepo.create({
        ...form, purityKarat: Number(form.purityKarat), weightGrams: Number(form.weightGrams),
        makingChargeValue: Number(form.makingChargeValue), wastagePercent: Number(form.wastagePercent),
        costBasis: Number(form.costBasis),
        status: 'in_stock', customAttributes: {}, photos: [],
        tenantId: 'default', createdBy: 'admin', updatedBy: 'admin',
      } as any)
      setShowModal(false)
      setForm({ tagNumber: '', description: '', category: 'ring', metalType: 'gold', purityKarat: 18, weightGrams: 0, makingChargeType: 'per_gram', makingChargeValue: 0, wastagePercent: 0, costBasis: 0, showcaseLocation: '', notes: '' })
      await load()
    } catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) } finally { setSaving(false) }
  }

  const filtered = search
    ? items.filter((i: any) => (i.description ?? '').toLowerCase().includes(search.toLowerCase()) || (i.tagNumber ?? '').toLowerCase().includes(search.toLowerCase()))
    : items

  const totalValue = items.filter((i: any) => i.status === 'in_stock').reduce((s: number, i: any) => s + (i.costBasis ?? 0), 0)
  const inStock = items.filter((i: any) => i.status === 'in_stock').length

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="text-2xl font-bold text-gray-900">Inventory</h2><p className="text-sm text-gray-500 mt-1">{items.length} items · {inStock} in stock · Value: ₱{totalValue.toLocaleString()}</p></div>
        <Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>Add Item</Button>
      </div>
      <div className="mb-4"><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by tag or description..." /></div>
      <Card><CardHeader title="Item Catalog" />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Tag','Description','Category','Metal','Karat','Weight','Making Charge','Wastage','Cost','Status'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? <tr><td colSpan={10} className="py-8 text-center text-gray-400">Loading...</td></tr> :
             filtered.length === 0 ? <tr><td colSpan={10} className="py-8 text-center text-gray-400">No items.</td></tr> :
             filtered.map((i: any) => (
              <tr key={i.id} className="hover:bg-gray-50"><td className="px-3 py-2.5 font-mono text-xs">{i.tagNumber}</td><td className="px-3 py-2.5 font-medium">{i.description}</td><td className="px-3 py-2.5"><Badge color="purple">{ITEM_CATEGORY_LABELS[i.category as keyof typeof ITEM_CATEGORY_LABELS]??i.category}</Badge></td><td className="px-3 py-2.5 text-xs">{METAL_TYPE_LABELS[i.metalType as keyof typeof METAL_TYPE_LABELS]??i.metalType}</td><td className="px-3 py-2.5 text-center">{i.purityKarat}K</td><td className="px-3 py-2.5 text-right">{i.weightGrams}g</td><td className="px-3 py-2.5 text-xs">{MAKING_CHARGE_LABELS[i.makingChargeType as keyof typeof MAKING_CHARGE_LABELS]??i.makingChargeType}: ₱{(i.makingChargeValue??0).toLocaleString()}</td><td className="px-3 py-2.5 text-right">{i.wastagePercent}%</td><td className="px-3 py-2.5 text-right font-medium">₱{(i.costBasis??0).toLocaleString()}</td><td className="px-3 py-2.5"><Badge color={i.status==='in_stock'?'green':i.status==='sold'?'blue':'yellow'}>{ITEM_STATUS_LABELS[i.status as keyof typeof ITEM_STATUS_LABELS]??i.status}</Badge></td></tr>
            ))}
          </tbody>
        </table></div>
      </Card>
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Item" size="lg"
        footer={<div className="flex gap-2"><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={save} loading={saving} icon={<Gem className="h-4 w-4"/>}>Add Item</Button></div>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Tag Number" value={form.tagNumber} onChange={e => setForm({...form, tagNumber: e.target.value})} />
          <Input label="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} required />
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Category</label><select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">{Object.entries(ITEM_CATEGORY_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Metal</label><select value={form.metalType} onChange={e => setForm({...form, metalType: e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">{Object.entries(METAL_TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          <Input label="Karat" type="number" value={String(form.purityKarat)} onChange={e => setForm({...form, purityKarat: Number(e.target.value)})} />
          <Input label="Weight (grams)" type="number" value={String(form.weightGrams)} onChange={e => setForm({...form, weightGrams: Number(e.target.value)})} />
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Making Charge Type</label><select value={form.makingChargeType} onChange={e => setForm({...form, makingChargeType: e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">{Object.entries(MAKING_CHARGE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          <Input label="Making Charge Value" type="number" value={String(form.makingChargeValue)} onChange={e => setForm({...form, makingChargeValue: Number(e.target.value)})} />
          <Input label="Wastage %" type="number" value={String(form.wastagePercent)} onChange={e => setForm({...form, wastagePercent: Number(e.target.value)})} />
          <Input label="Cost Basis (₱)" type="number" value={String(form.costBasis)} onChange={e => setForm({...form, costBasis: Number(e.target.value)})} />
          <Input label="Showcase Location" value={form.showcaseLocation} onChange={e => setForm({...form, showcaseLocation: e.target.value})} />
        </div>
      </Modal>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// POS / Sales
// ═══════════════════════════════════════════════════════════════

export function JewelryPOSPage() {
  const [items, setItems] = useState<any[]>([])
  const [rateCards, setRateCards] = useState<any[]>([])
  const [customers, setCustomers] = useState<any[]>([])
  const [cart, setCart] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showPrice, setShowPrice] = useState<any>(null)
  const [orderForm, setOrderForm] = useState({ customerName: '', paymentMethod: 'cash', amountTendered: 0, notes: '' })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [i, r, c] = await Promise.all([
        jewelryItemRepo.findMany({ page: 1, pageSize: 500 }),
        rateCardRepo.findMany({ page: 1, pageSize: 10, sort: [{ field: 'effectiveAt', direction: 'desc' }] }),
        jewelryCustomerRepo.findMany({ page: 1, pageSize: 500 }),
      ])
      setItems('items' in i ? i.items : [])
      setRateCards('items' in r ? r.items : [])
      setCustomers('items' in c ? c.items : [])
    } catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  function addToCart(item: any) {
    const rate = rateCards.find((r: any) => r.metalType === item.metalType && r.purityKarat === item.purityKarat)
    if (!rate) { alert('No rate card found for this metal/purity. Add a rate card first.'); return }
    const breakdown = PriceCalculator.calculate({ item, rateCard: rate })
    setCart([...cart, { ...item, lineTotal: breakdown.grandTotal, rateUsed: rate.ratePerGram }])
  }

  function removeFromCart(idx: number) {
    setCart(cart.filter((_, i) => i !== idx))
  }

  async function completeOrder() {
    if (cart.length === 0) { alert('Cart is empty'); return }
    const grandTotal = cart.reduce((s, c) => s + c.lineTotal, 0)
    try {
      await salesOrderRepo.create({
        customerName: orderForm.customerName || 'Walk-in Customer',
        orderNumber: 'SO-' + new Date().toISOString().slice(0, 10).replace(/-/g, '') + '-' + Math.random().toString(36).slice(2, 6).toUpperCase(),
        status: 'completed', subtotal: grandTotal, discountTotal: 0, oldGoldCreditTotal: 0,
        taxTotal: 0, grandTotal, paymentMethod: orderForm.paymentMethod,
        amountTendered: Number(orderForm.amountTendered) || grandTotal,
        changeGiven: Math.max(0, (Number(orderForm.amountTendered) || grandTotal) - grandTotal),
        requiresKyc: false, cashPortion: orderForm.paymentMethod === 'cash' ? grandTotal : 0,
        completedAt: Date.now(), notes: orderForm.notes,
        tenantId: 'default', createdBy: 'admin', updatedBy: 'admin',
      } as any)

      // Update items to sold
      for (const c of cart) {
        try { await jewelryItemRepo.update(c.id, { status: 'sold', updatedBy: 'admin' } as any) }
        catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) }
      }

      alert(`Order completed! Total: ₱${grandTotal.toLocaleString()}`)
      setCart([])
      setOrderForm({ customerName: '', paymentMethod: 'cash', amountTendered: 0, notes: '' })
      await load()
    } catch (e: any) { alert(mapErrorToUserMessage(e).userMessage) }
  }

  const grandTotal = cart.reduce((s, c) => s + c.lineTotal, 0)

  return (
    <div className="p-6">
      <div className="mb-6"><h2 className="text-2xl font-bold text-gray-900">Point of Sale</h2><p className="text-sm text-gray-500 mt-1">{items.filter((i:any)=>i.status==='in_stock').length} items available</p></div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Item Selection */}
        <div className="lg:col-span-2">
          <Card><CardHeader title="Available Items" description="Click to add to cart" />
            <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
              {items.filter((i: any) => i.status === 'in_stock').map((item: any) => (
                <div key={item.id} className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer" onClick={() => addToCart(item)}>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.description}</p>
                    <p className="text-xs text-gray-500">{item.tagNumber} · {item.weightGrams}g · {item.purityKarat}K {item.metalType} · {ITEM_CATEGORY_LABELS[item.category as keyof typeof ITEM_CATEGORY_LABELS] ?? item.category}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-green-700">₱{(item.costBasis ?? 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-400">Cost basis</p>
                  </div>
                </div>
              ))}
              {items.filter((i: any) => i.status === 'in_stock').length === 0 && <p className="p-4 text-sm text-gray-400">No items in stock.</p>}
            </div>
          </Card>
        </div>

        {/* Cart */}
        <div>
          <Card><CardHeader title="Cart" description={`${cart.length} items`} />
            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
              {cart.map((c, idx) => (
                <div key={idx} className="flex justify-between p-3 hover:bg-gray-50">
                  <div>
                    <p className="text-xs font-medium">{c.description}</p>
                    <p className="text-xs text-gray-400">Rate: ₱{c.rateUsed}/g</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">₱{c.lineTotal.toLocaleString()}</p>
                    <button onClick={() => removeFromCart(idx)} className="text-xs text-red-500">Remove</button>
                  </div>
                </div>
              ))}
              {cart.length === 0 && <p className="p-4 text-sm text-gray-400">Click items to add</p>}
            </div>
            <div className="border-t p-4">
              <div className="flex justify-between text-lg font-bold">
                <span>Total:</span>
                <span className="text-green-700">₱{grandTotal.toLocaleString()}</span>
              </div>
            </div>
          </Card>

          {/* Checkout */}
          <div className="mt-4 space-y-3">
            <Input label="Customer Name" value={orderForm.customerName} onChange={e => setOrderForm({...orderForm, customerName: e.target.value})} placeholder="Walk-in Customer" />
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <select value={orderForm.paymentMethod} onChange={e => setOrderForm({...orderForm, paymentMethod: e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                {Object.entries(PAYMENT_METHOD_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            <Input label="Amount Tendered" type="number" value={String(orderForm.amountTendered)} onChange={e => setOrderForm({...orderForm, amountTendered: Number(e.target.value)})} />
            {Number(orderForm.amountTendered) > grandTotal && <p className="text-sm text-green-600">Change: ₱{(Number(orderForm.amountTendered) - grandTotal).toLocaleString()}</p>}
            <Button onClick={completeOrder} className="w-full" icon={<ShoppingBag className="h-4 w-4"/>}>Complete Sale</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
